import { isEmpty, map } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import { ROLE_TO_DISPLAY_NAME } from "@app/analytics/account/constants";
import MenuItem from "@app/analytics/account/WorkspaceSettings/components/MenuItem";
import OptionsMenuCell from "@app/analytics/account/WorkspaceSettings/components/OptionsMenuCell";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableRow from "@app/analytics/components/Table/TableRow";
import ROUTES from "@app/common/routes";
import { pluralize } from "@app/common/utils";
import {
  EnterpriseTeamUser,
  EnterpriseWorkspaceTeam,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";
import { isWorkspaceMember } from "@app/common/workspaces/workspaceUtils";

interface TeamsTableRowProps {
  currentUser: EnterpriseWorkspaceUser;
  onLeaveTeam: (teamId: string) => void;
  team: EnterpriseWorkspaceTeam;
  workspaceId: string;
}

const getTeamManagerNames = (users: EnterpriseTeamUser[]) => {
  const teamManagers = users.filter((user) => user.role === "manager");
  if (isEmpty(teamManagers)) {
    return "-";
  }

  return map("fullName", teamManagers).join(", ");
};

const TeamsTableRow = ({
  currentUser,
  onLeaveTeam,
  team,
  workspaceId,
}: TeamsTableRowProps) => {
  const navigate = useNavigate();
  const numTeamMembers = team.users.length + (team.invites?.length ?? 0);
  const navigateToTeam = () =>
    navigate(ROUTES.workspaceTeamById(workspaceId, team.id));

  const isMember = isWorkspaceMember(currentUser);
  const teamRole =
    team.users.find((user) => user.userId === currentUser.userId)?.role ??
    "member";

  return (
    <TableRow
      key={team.id}
      onClick={navigateToTeam}
      sx={{ "&:hover": { bgcolor: "states.hover", cursor: "pointer" } }}
    >
      <TableCell>{team.name}</TableCell>
      <TableCell>
        {numTeamMembers} {pluralize("member", numTeamMembers)}
      </TableCell>
      <TableCell sx={{ whiteSpace: "wrap" }}>
        {getTeamManagerNames(team.users)}
      </TableCell>
      {isMember && (
        <>
          <TableCell>{ROLE_TO_DISPLAY_NAME[teamRole]}</TableCell>
          <OptionsMenuCell
            id="detail-menu"
            menuItems={[
              <MenuItem key={`view-team-${team.id}`} onClick={navigateToTeam}>
                View Team
              </MenuItem>,
              <MenuItem
                key={`leave-team-${team.id}`}
                onClick={() => onLeaveTeam(team.id)}
              >
                Leave this Team
              </MenuItem>,
            ]}
          />
        </>
      )}
    </TableRow>
  );
};

export default TeamsTableRow;
